import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import memoize from 'fast-memoize';
import { Translate, I18n } from 'react-redux-i18n';
import { compose } from 'redux';
import { THEMES } from '../../../../../configs/LayoutConfig';
import { withComponentId } from '../../../../common/ComponentIdContext';
import PortalTooltip from '../../../../common/portal-tooltip/PortalTooltip';
import { formatTextFloat, formatValueBillion } from '../../../../helpers/Text';
import { LEVELS, TYPES } from '../../Const';
import Table from '../../../../common/table/Table';
import { removeNullItem } from '../../helper';
import { incomeStatementInsuranceMapping } from '../../income-statement/InsuranceConst';
import { REDUCER_NAME, updateSkip } from '../../reducer';
import MiniBarChart from './MiniBarChart';
import StackChart from './StackChart';
import { incomeStatementMapping } from '../../income-statement/Const';
import { incomeStatementBankingMapping } from '../../income-statement/BankingConst';

const Plus = require('../../../../../assets/images/svg/fundamental/plus.svg');
const Minus = require('../../../../../assets/images/svg/fundamental/minus.svg');
const PlusBlack = require('../../../../../assets/images/svg/fundamental/plus_black.svg');
const MinusBlack = require('../../../../../assets/images/svg/fundamental/minus_black.svg');

const expandIcon = {
  Dark: {
    MinusImg: Minus,
    PlusImg: Plus,
  },
  Light: {
    MinusImg: MinusBlack,
    PlusImg: PlusBlack,
  },
  Classic: {
    MinusImg: Minus,
    PlusImg: Plus,
  },
};

const mapStatus = {
  [undefined]: true,
  [true]: false,
  [false]: true,
};

const ignoreFormatRows = [
  incomeStatementMapping.EPSBasis,
  incomeStatementMapping.EPSDiluted,
  incomeStatementBankingMapping.basicEarningsPerShareForProfitAttributableToTheEquityHolders,
  incomeStatementInsuranceMapping.EPS_basis,
];

const getAllAvailableIds = (rowGroup, dataByIds, id) => {
  if (!dataByIds[id].children || !rowGroup[id]) {
    return [id];
  }

  const allChild = dataByIds[id].children.reduce((result, childId) => {
    return result.concat(getAllAvailableIds(rowGroup, dataByIds, childId));
  }, []);

  return [id, ...allChild];
};

const memoizedGetAllAvailableIds = memoize(getAllAvailableIds);

const getRowGroup = (ids, dataByIds) => {
  return ids.reduce((result, id) => {
    if (!Array.isArray(dataByIds[id].children)) {
      return result;
    }

    return {
      ...result,
      ...getRowGroup(dataByIds[id].children, dataByIds),
      [id]: false,
    };
  }, {});
};

class FinancialStatementTable extends PureComponent {
  constructor(props) {
    super(props);
    const { ids, dataByIds } = this.props;

    this.state = {
      rowGroup: getRowGroup(ids, dataByIds),
      isExpandAll: false,
    };
    this.scrollRef = React.createRef();
  }

  componentWillReceiveProps(nextProps) {
    const { ids } = this.props;
    if (!ids.length && nextProps.ids.length) {
      this.setState({
        rowGroup: getRowGroup(nextProps.ids, nextProps.dataByIds),
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { organCode } = this.props;
    if (organCode !== prevProps.organCode) {
      this.scrollRef.current && this.scrollRef.current.scrollToTop();
    }
  }

  onPrevSchema = () => {
    const { skip, updateSkip, componentId } = this.props;
    updateSkip(componentId, skip + 1);
  };

  onNextSchema = () => {
    const { updateSkip, skip, componentId } = this.props;
    updateSkip(componentId, skip - 1);
  };

  renderTimeColumn = (data, item) => {
    if (item.type === TYPES.CHART) {
      return data && typeof data === 'object' ? (
        <StackChart titles={item.titles} data={removeNullItem(data)} />
      ) : null;
    }
    let text = '--';
    if (data !== 0) {
      text = ignoreFormatRows.includes(item.id)
        ? data
        : formatTextFloat(formatValueBillion(data));
    }
    if (data === null) {
      text = '--';
    }

    return <div className="text-right">{text}</div>;
  };

  getSchemaTitle = item => {
    if (!item.quarter || !item.year) {
      return <Translate value="financialStatement.noHeadTitle" />;
    }
    const { timeKey } = this.props;
    const key =
      timeKey === 'yearly'
        ? 'financialStatement.yearName'
        : 'financialStatement.quarterName';

    return <Translate quarter={item.quarter} year={item.year} value={key} />;
  };

  getFields = () => {
    const { fields, numberColumn, skip } = this.props;
    if (!fields.length) {
      return [...Array(5)].map((_, index) => ({ key: index }));
    }

    return fields.slice(
      Math.max(fields.length - skip - numberColumn, 0),
      fields.length - skip,
    );
  };

  getFlexibleSchema = () => {
    const { fields, numberColumn, skip } = this.props;
    const isEnablePrev = fields.length - skip - numberColumn > 0;
    const isEnableNext = skip > 0;
    return this.getFields().map((item, index) => {
      const className = 'd-flex align-items-center justify-content-end';
      const thStyle = { width: '90px' };
      if (index === 0) {
        return {
          isHtml: true,
          disableSort: true,
          thStyle,
          title: (
            <div className={className}>
              {isEnablePrev && (
                <i
                  onClick={this.onPrevSchema}
                  className="icon-caret-left mr-5"
                />
              )}
              {this.getSchemaTitle(item)}
            </div>
          ),
          thTooltip: this.getSchemaTitle(item),
          key: item.key,
          render: this.renderTimeColumn,
        };
      }
      if (index === numberColumn - 1) {
        return {
          isHtml: true,
          disableSort: true,
          thStyle,
          title: (
            <div className={className}>
              {this.getSchemaTitle(item)}
              {isEnableNext && (
                <i
                  onClick={this.onNextSchema}
                  className="icon-caret-right ml-5"
                />
              )}
            </div>
          ),
          thTooltip: this.getSchemaTitle(item),
          key: item.key,
          render: this.renderTimeColumn,
        };
      }

      return {
        disableSort: true,
        isHtml: true,
        thStyle,
        key: item.key,
        title: <div className={className}>{this.getSchemaTitle(item)}</div>,
        thTooltip: this.getSchemaTitle(item),
        render: this.renderTimeColumn,
      };
    });
  };

  getPlusIcon = () => {
    const { theme } = this.props;
    return expandIcon[theme].PlusImg;
  };

  getMinusIcon = () => {
    const { theme } = this.props;
    return expandIcon[theme].MinusImg;
  };

  getExpandIcon = bool => {
    const { isExpandAll } = this.state;
    const MinusImg = this.getMinusIcon();
    const PlusImg = this.getPlusIcon();
    return (
      <img
        className="expand-status-icon"
        src={bool || isExpandAll ? MinusImg : PlusImg}
        alt="status"
      />
    );
  };

  toggleExpandStatus = id => {
    const { rowGroup, isExpandAll } = this.state;
    const { dataByIds } = this.props;
    if (!dataByIds[id].children) {
      return;
    }

    this.setState({
      isExpandAll: mapStatus[rowGroup[id]] ? isExpandAll : false,
      rowGroup: {
        ...rowGroup,
        [id]: mapStatus[rowGroup[id]],
      },
    });
  };

  getRowWidth = (width, level) => {
    return width - (level - 1) * 15 - 35;
  };

  renderRowTitle = (text, item, width) => {
    const { rowGroup } = this.state;
    const { i18nPrefix } = this.props;
    if (!width) {
      return null;
    }
    if (item.type === TYPES.CHART) {
      return (
        <div className="list-color">
          {item.titles.map(({ color, i18nKey }) => (
            <div key={color} className="d-flex align-items-center color-item">
              <div style={{ backgroundColor: color }} className="circle" />
              <PortalTooltip tooltip={I18n.t(i18nKey)}>
                <div
                  className="text-truncate"
                  style={{
                    width: width - 30, //minus padding
                  }}
                >
                  {I18n.t(i18nKey)}
                </div>
              </PortalTooltip>
            </div>
          ))}
        </div>
      );
    }

    return (
      <PortalTooltip
        tooltip={<Translate value={`${i18nPrefix}.${item.i18nKey}`} />}
      >
        <div
          onClick={() => this.toggleExpandStatus(item.id)}
          className={`fs-table-row level-${item.level}`}
        >
          {item.children && this.getExpandIcon(rowGroup[item.id])}
          <div
            className="text-truncate"
            style={{
              width: this.getRowWidth(width, item.level),
            }}
          >
            {I18n.t(`${i18nPrefix}.${item.i18nKey}`)}
          </div>
        </div>
      </PortalTooltip>
    );
  };

  renderTrend = (text, item) => {
    const data = this.getFields().map(field => ({
      x: field.key,
      y: item[field.key],
    }));

    return item.type === TYPES.CHART ? '' : <MiniBarChart data={data} />;
  };

  toggleExpandAll = () => {
    const { isExpandAll, rowGroup } = this.state;
    const newRowGroup = Object.keys(rowGroup).reduce((result, id) => {
      return {
        ...result,
        [id]: !isExpandAll,
      };
    }, {});

    this.setState({
      rowGroup: newRowGroup,
      isExpandAll: !isExpandAll,
    });
  };

  getSchema = () => {
    const { isExpandAll } = this.state;
    const { isAllParent } = this.props;
    const schema = [
      {
        disableSort: true,
        key: 'text',
        isHtml: true,
        title: (
          <div className="d-flex">
            {isAllParent ? (
              <div className="d-inline-block" style={{ width: 11 }} />
            ) : (
              <img
                className="expand-all"
                onClick={this.toggleExpandAll}
                src={isExpandAll ? Minus : Plus}
                alt="expand"
              />
            )}
            <Translate value="financialStatement.item" />
          </div>
        ),
        thTooltip: <Translate value="financialStatement.item" />,
        render: this.renderRowTitle,
      },
      {
        disableSort: true,
        key: 'trend',
        title: 'financialStatement.trend',
        thStyle: {
          width: '25px',
        },
        render: this.renderTrend,
      },
    ];
    return schema.concat(this.getFlexibleSchema());
  };

  getIds = () => {
    const { rowGroup, isExpandAll } = this.state;
    const { dataByIds, ids } = this.props;
    return ids.reduce((result, id) => {
      if (dataByIds[id].level === LEVELS.PARENT) {
        if (rowGroup[id] || isExpandAll) {
          return result.concat(
            memoizedGetAllAvailableIds(rowGroup, dataByIds, id, isExpandAll),
          );
        }

        return result.concat(id);
      }

      return result;
    }, []);
  };

  render() {
    const { isLoading, getDataFromRedux } = this.props;

    return (
      <div className="d-flex flex-fill">
        <Table
          scrollRef={this.scrollRef}
          isLoading={isLoading}
          resizeable={false}
          getDataFromRedux={getDataFromRedux}
          ids={this.getIds()}
          schema={this.getSchema()}
        />
      </div>
    );
  }
}

FinancialStatementTable.propTypes = {
  ids: PropTypes.array.isRequired,
  timeKey: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  i18nPrefix: PropTypes.string,
  height: PropTypes.number,
  getDataFromRedux: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired,
  isAllParent: PropTypes.bool,
  organCode: PropTypes.string,
  updateSkip: PropTypes.func.isRequired,
  onStartIndexChange: PropTypes.func,
};

FinancialStatementTable.defaultProps = {
  i18nPrefix: 'financialStatement',
  isLoading: false,
  isAllParent: false,
};

const mapStateToProps = (state, { componentId }) => ({
  organCode: state[REDUCER_NAME][componentId].currentSearch.code,
  skip: state[REDUCER_NAME][componentId].skip,
  theme: state.theme,
  i18n: state.i18n,
});

const mapDispatchToProps = {
  updateSkip,
};

export default compose(
  withComponentId,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(FinancialStatementTable);
